import { isNodeJS } from "../../compat";

/**
 * Available priority step. Layers will need to play well, guidance is TOPMOST = always visible, OFF = never
 * For others, meaning and handling is left to the layer. With ABOVE likely being a contextual or immediate action.
 */
export const PRIORITY = {
  TOPMOST: 4,
  ABOVE: 3,
  NORMAL: 2,
  BELOW: 1,
  OFF: 0
};
export const PRIORITIES = Object.freeze([PRIORITY.TOPMOST, PRIORITY.ABOVE, PRIORITY.NORMAL, PRIORITY.BELOW, PRIORITY.OFF]);

/** Compositing context. Allows to query the ongoing composition.  */
export class LabelCompositor {
  /** @type {CollisionTree} QuadTree accelerated structure for collision of 2D rectangles */
  collisionTree;
  /** @type {RollupController} Controls rollups target and deferred placement */
  rollups;

  constructor(viewer) {
    this.viewer = viewer;
    this.rollups = new RollupController();
    this.isLoaded = false;
    this.wireframe = !isNodeJS() && window?.localStorage.getItem('hud-collisions-wireframe') === 'true';

    // prefill initial facet configuration while we load.
    this.rollups.setRollups([], [], true);

    // sometimes CompGeom is not available yet, in which case we want to load it and attach when it is ready
    this.viewer.loadExtension('Autodesk.Tandem.CompGeom').then(() => {
      const { clientHeight, clientWidth } = this.viewer.getCamera();
      this.collisionTree = new CollisionTree(clientWidth, clientHeight);

      // Allows debugging collision by drawing UI collision rectangles.
      if (this.wireframe) {
        const canvas = this.collisionTree.getWireframeCanvas(this.viewer.getDocument());
        this.viewer.canvasWrap.after(canvas);
      }

      this.isLoaded = true;
    });
  }

  startFrame() {
    this.collisionTree.start();
  }

  endFrame() {
    this.collisionTree.reset();

    // Important to set visibility here, as it was delayed in case we pulled it back.
    this.rollups.forEachPlaced(({ ui }) => ui.setVisible(true));
    this.rollups.reset();
  }

  resize(updatedSize) {
    this.collisionTree.resizeOnNextReset(updatedSize);
  }

  /* Checks if the bounds overlap the visible client space. */
  onScreen(bounds) {
    const { clientWidth, clientHeight } = this.viewer.getCamera();
    // intersectsBox last param are inclusive, so using an epsilon.
    return intersectsBox(bounds, 0, 0, clientWidth - 1E-4, clientHeight - 1E-4);
  }

  /** Adds label to the composition. No rollup / offscreen / collision checks */
  place(target) {
    this.collisionTree.insert(target);
  }

  /**
   * Adds an asset label to the composition, pulling it back and rolling it up if needed.
   * Manages UI visibility as needed.
   */
  placeAsset(target) {

    // if it can't be pulled back, then we don't care about collisions.
    const shouldPlace = !target.withPullback || !this.collisionTree.hasCollisions(target);
    if (shouldPlace) {
      this.collisionTree.insert(target);
      this.rollups.addPlaced(target);
      // will be made visible at end of frame, if it remained placed.
      return;
    }

    // We know we can't place it, the only question is what previous asset labels do we pull back too.
    this.rollups.addDeferred(target.ui.data);
    target.ui.setVisible(false);
    this.rollups.pullbackAllFacets(target, (target) => {
      this.collisionTree.remove(target);
      target.ui.setVisible(false);
    });
  }

  updateNextRollupConfig(hasOnlyModelContext, lvlTargets, spcTargets) {
    this.rollups.setRollups(lvlTargets, spcTargets, hasOnlyModelContext);
  }
}

/**
 * @typedef {Object} RollupStatus
 * @property {Array} [deferred] - Deferred UI metadata
 * @property {Array} [placed] - Collision targets for placed and visible UIs
 * @property {Array} [offscreen] - Collision targets for UIs outside the viewport
 * @property {boolean} [wasPulledBack] - Whether UIs for this facet should be directly deferred.
 */

/** Manages deferral accounting for a composition. (what asset label should be rollup when) */
class RollupController {

  /**
   * Heuristic to de-clutter a fully visible facility, struggles in FPV.
   * @type {boolean}
   */
  hasOnlyModelContext = false;

  /**
   * Rollups per location target (maps are keyed by facetIDs)
   * @type {{rooms: Map<string, RollupStatus>, levels: Map<string, RollupStatus>, global: RollupStatus}}
   */
  rollups = {
    rooms: new Map(),
    levels: new Map(),
    global: {}
  };

  /** Declares this set of levels and rooms as valid rollup targets. */
  setRollups(levels, spaces, hasOnlyModelContext) {
    this.hasOnlyModelContext = hasOnlyModelContext;
    this.rollups.rooms.clear();
    spaces.forEach((id) => this.rollups.rooms.set(id, {}));
    this.rollups.levels.clear();
    levels.forEach((id) => this.rollups.levels.set(id, {}));
  }

  /** Resets valid rollup targets and any cached data. */
  reset() {
    for (const id of this.rollups.rooms.keys()) this.rollups.rooms.set(id, {});
    for (const id of this.rollups.levels.keys()) this.rollups.levels.set(id, {});
    this.rollups.global = {};
  }

  /** Runs callback on each UI that are still placed. */
  forEachPlaced(fn) {
    const applyToAllPlaced = ({ placed }) => {
      if (!placed) return;
      // placed can have null entries if some were pulled back.
      for (const target of placed) if (target) fn(target);
    };

    this.rollups.rooms.forEach(applyToAllPlaced);
    this.rollups.levels.forEach(applyToAllPlaced);
    applyToAllPlaced(this.rollups.global);
  }

  addPlaced(target) {
    target.numFacetsPlacedIn = 0;

    let rollups = this.#getMostSpecificRollups(target.ui.data);
    for (const rollup of rollups) {
      // Some locations for multi-location assets might be deferred. We still need to defer these the asset in these locations.
      if (rollup.wasPulledBack && target.withPullback) {
        appendRollup(rollup, 'deferred', target.ui.data);
        continue;
      }

      appendRollup(rollup, 'placed', target);
      target.numFacetsPlacedIn++;
    }
  }

  /** Marks a UI's metadata to be handled by a later rollup. Does not imply that future similar ui will be deferred too. */
  addDeferred(metadata) {
    let rollups = this.#getMostSpecificRollups(metadata);
    for (const rollup of rollups) {
      appendRollup(rollup, 'deferred', metadata);
    }
  }

  addOffscreen(target) {
    let rollups = this.#getMostSpecificRollups(target.ui.data);
    for (const rollup of rollups) {
      appendRollup(rollup, 'offscreen', target);
    }
  }

  getDeferredForRoom(facetId) {
    const metadata = this.rollups.rooms.get(facetId);
    return metadata?.deferred ?? [];
  }

  getDeferredForLevel(facetId) {
    const metadata = this.rollups.levels.get(facetId);
    return metadata?.deferred ?? [];
  }

  getDeferredForGlobal() {
    return this.rollups.global.deferred ?? [];
  }

  /** Go through `withPullback` targets, on the target's facets, and pull them to be deferred if needed. */
  pullbackAllFacets(target, fnCleanup) {
    // We now chose to pull back an asset label from all its valid and applicable rollups. This has the disadvantage
    // of being over conservative, and potentially rolling up locations which would not need it otherwise. The issue
    // is that without a 3D check, it is ambiguous which of the asset's multiple locations is impossible to place.
    // We used to leave labels placed when ambiguous, but that lead to more confusion.

    this.#getMostSpecificRollups(target.ui.data).forEach((m) => pullback(m, fnCleanup));
  }

  /** Checks if the facet has already been marked for pullback, or if the asset is a room / level itself. */
  shouldBeDeferred(metadata) {
    // Rooms & Levels auto-deferrals (ie. isSpatial) is now upstream of this.

    // `hasOnlyModelContext` is for an unfiltered facility (everything). Otherwise, if EVERY facet of the asset
    // was pulled back (1 in case of 1) or (all in case of multiple) then we defer it.
    // if the asset has multiple facets, and only SOME were pulled back, then do NOT defer it,
    // as-it might be able to be placed in one of them.
    return this.hasOnlyModelContext ||
    this.#getMostSpecificRollups(metadata).every((m) => m.wasPulledBack);
  }

  #getMostSpecificRollups({ spacesFacetId, levelsFacetId }) {
    let rollups;

    // disallow room rollups if we are in summary view.
    if (!this.hasOnlyModelContext) {
      rollups = getRollups(spacesFacetId, this.rollups.rooms);
      if (rollups.length) return rollups;
    }

    rollups = getRollups(levelsFacetId, this.rollups.levels);
    if (rollups.length) return rollups;

    return [this.rollups.global];
  }
}

function appendRollup(rollup, key, item) {
  if (rollup[key]) {
    rollup[key].push(item);
  } else {
    rollup[key] = [item];
  }
}

function getRollups(facetsId, rollupMap) {
  if (!facetsId) return [];

  const rollups = [];
  for (const id of facetsId) {
    const rollup = rollupMap.get(id);
    if (rollup) rollups.push(rollup);
  }
  return rollups;
}

function pullback(rollup, fnCleanup) {
  rollup.wasPulledBack = true;

  if (rollup.placed) {
    for (let i = 0; i < rollup.placed.length; i++) {
      const placed = rollup.placed[i];
      if (!placed?.withPullback) continue; // already pulled back or forced placed.

      rollup.placed[i] = null;
      appendRollup(rollup, 'deferred', placed.ui.data);

      // if the asset is in multiple facets, we do the opposite of shouldBeDeferred meaning that we only remove it
      // from being visible when it is pulled back from the last facet it was placed in.
      placed.numFacetsPlacedIn -= 1;
      if (placed.numFacetsPlacedIn === 0) {
        fnCleanup(placed);
      }
    }
  }

  if (rollup.offscreen) {
    for (let i = 0; i < rollup.offscreen.length; i++) {
      const offscreen = rollup.offscreen[i];
      if (!offscreen?.withPullback) continue; // already pulled back or forced placed.

      rollup.offscreen[i] = null;
      appendRollup(rollup, 'deferred', offscreen.ui.data);
    }
  }
}

const COLOR_PINK = 'rgba(255, 0, 127, 0.75)';
const COLOR_BROWN = 'rgba(100, 72, 60, 0.85)';
const collisions = [];
const TOL = 1E-6;
export class CollisionTree {
  #tree;
  #wireframeCanvas;
  #wireframeCtx;

  /** @type {?{ width: number, height: number }} New canvas bounds to use on next reset */
  updatedSize;

  constructor(width, height) {
    const cg = Autodesk.Tandem.CompGeom;
    console.assert(cg, 'Collisions require CompGeom to be loaded');

    const itemHandler = {
      intersectsBox,
      getPoint: (pRect, outPoint) => {
        outPoint.x = pRect.x + pRect.width * 0.5;
        outPoint.y = pRect.y + pRect.height * 0.5;
        return outPoint;
      }
    };

    this.#tree = new cg.QuadTree(0, 0, width, height, TOL, itemHandler);
  }

  insert(pRect) {
    if (this.#wireframeCtx) {
      const { x, y, width, height } = pRect;
      if (this.hasCollisions(pRect)) {
        this.#wireframeCtx.fillRect(x, y, width, height);
      } else {
        this.#wireframeCtx.strokeRect(x, y, width, height);
      }
    }

    this.#tree.addItem(pRect);
  }

  remove(pRect) {
    if (this.#wireframeCtx) {
      const { x, y, width, height } = pRect;
      const tmp = this.#wireframeCtx.fillStyle;
      this.#wireframeCtx.fillStyle = COLOR_BROWN;
      this.#wireframeCtx.fillRect(x, y, width, height);
      this.#wireframeCtx.fillStyle = tmp;
    }

    this.#tree.deleteItem(pRect);
  }

  hasCollisions({ x, y, width, height }) {
    let anyCollision = false;
    this.#tree.enumInBox(x, y, x + width, y + height, () => anyCollision = true);
    return anyCollision;
  }

  getCollisions({ x, y, width, height }) {
    collisions.length = 0;
    this.#tree.enumInBox(x, y, x + width, y + height, (el) => collisions.push(el));
    return collisions;
  }

  start() {
    if (this.#wireframeCtx) {
      const { minx, miny, maxx, maxy } = this.#tree;
      this.#wireframeCtx.clearRect(minx, miny, maxx, maxy);
    }
  }

  reset() {
    this.#tree.clear();

    if (this.updatedSize) {
      this.maxx = this.updatedSize.width;
      this.maxy = this.updatedSize.height;

      this.updatedSize = null;
    }
  }

  resizeOnNextReset(size) {
    this.updatedSize = size;

    if (this.#wireframeCanvas) {
      this.#wireframeCanvas.width = size.width;
      this.#wireframeCanvas.height = size.height;
    }
  }

  getWireframeCanvas(document) {
    const canvas = this.#wireframeCanvas = document.createElement('canvas');
    canvas.id = 'collision-wireframe';
    canvas.width = this.maxx;
    canvas.height = this.maxy;
    canvas.style = `
			position: absolute;
			width: 100%;
			height: 100%;
			left: 0;
			top: 0;
			pointer-events: none;
		`;

    this.#wireframeCanvas = canvas;
    this.#wireframeCtx = canvas.getContext("2d");

    setInterval(() => {
      this.#wireframeCtx.strokeStyle = COLOR_PINK;
      this.#wireframeCtx.fillStyle = COLOR_PINK;
    });

    return this.#wireframeCanvas;
  }
}

// (xmax and ymax) are inclusive.
function intersectsBox(rect, xmin, ymin, xmax, ymax) {
  const { x, y, width, height } = rect;
  return x <= xmax && y <= ymax && x + width > xmin && y + height > ymin;
}